import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import StudentLayout from "@/components/StudentLayout";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareWarning, Send, Clock, CheckCircle2, Paperclip, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ATTACH_MIMES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const ATTACH_MAX = 10 * 1024 * 1024;

export default function StudentAbsenceExplanation() {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [session, setSession] = useState("all_day");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/student/absence-explanations");
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load past leave requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ATTACH_MIMES.includes(f.type)) { toast.error("Leave letter must be a JPG, PNG or PDF."); e.target.value = ""; return; }
    if (f.size > ATTACH_MAX) { toast.error("File too large. Max 10 MB."); e.target.value = ""; return; }
    setFile(f);
  };

  const clearFile = () => { setFile(null); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("Please describe your reason for leave.");
    if (toDate < fromDate) return toast.error("'To date' cannot be before 'From date'.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("from_date", fromDate);
      fd.append("to_date", toDate);
      fd.append("session", session);
      fd.append("reason", reason.trim());
      if (file) fd.append("file", file);
      await api.post("/student/absence-explanation", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Leave request sent to your teacher.");
      setReason("");
      clearFile();
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not submit");
    } finally { setSubmitting(false); }
  };

  const openAttachment = async (id) => {
    try {
      const res = await api.get(`/student/absence-explanation/${id}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not open attachment");
    }
  };

  return (
    <StudentLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Leaves</div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Apply for leave & explain an absence</h1>
        <p className="text-slate-500 mt-1 text-sm">Add a reason and, if you have one, attach your leave letter. Your teacher reviews it.</p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
        <form onSubmit={submit} className="space-y-4" data-testid="absence-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">From date</label>
              <DatePicker value={fromDate} onChange={(d) => { setFromDate(d); if (toDate < d) setToDate(d); }} className="h-11 rounded-xl" data-testid="absence-from-date"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">To date</label>
              <DatePicker value={toDate} onChange={setToDate} className="h-11 rounded-xl" data-testid="absence-to-date"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Session</label>
              <Select value={session} onValueChange={setSession}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="absence-session"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_day">All day</SelectItem>
                  <SelectItem value="morning">Morning</SelectItem>
                  <SelectItem value="afternoon">Afternoon</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              rows={5}
              data-testid="absence-reason"
              placeholder="I was unwell with a fever and visited the doctor…"
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
            <div className="text-right text-xs text-slate-400 mt-1">{reason.length}/1000</div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Leave letter (optional)</label>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf" onChange={pickFile} className="hidden" data-testid="absence-file-input" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex-1 min-w-[200px]">
                {file ? (
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5" data-testid="absence-file-chip">
                    <FileText className="h-4 w-4 text-sky-500 shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1">{file.name}</span>
                    <span className="text-xs text-slate-400">{file.size < 1024*1024 ? `${Math.max(1, Math.round(file.size/1024))} KB` : `${(file.size/(1024*1024)).toFixed(1)} MB`}</span>
                    <button type="button" onClick={clearFile} className="text-slate-400 hover:text-rose-500" data-testid="absence-file-remove"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="h-11 rounded-xl" data-testid="absence-file-btn">
                    <Paperclip className="h-4 w-4 mr-1.5" /> Attach JPG, PNG or PDF
                  </Button>
                )}
              </div>
              <Button type="submit" disabled={submitting} data-testid="absence-submit"
                className="h-11 rounded-xl bg-sky-500 hover:bg-sky-600 shrink-0">
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} {submitting ? "Sending…" : "Send to teacher"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Max file size 10 MB.</p>
          </div>
        </form>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="absence-history">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
          <MessageSquareWarning className="h-4 w-4 text-amber-600" />
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Your previous leave requests</div>
            <div className="text-xs text-slate-500">Most recent first</div>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No leave requests submitted yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {items.map((it) => (
              <li key={it.id} className="px-5 py-3" data-testid={`absence-row-${it.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {it.from_date && it.to_date && it.from_date !== it.to_date
                      ? `${it.from_date} → ${it.to_date}`
                      : (it.from_date || it.date)} · <span className="capitalize">{(it.session || "—").replace("_", " ")}</span>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                    it.status === "reviewed"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}>
                    {it.status === "reviewed" ? <CheckCircle2 className="h-3 w-3"/> : <Clock className="h-3 w-3"/>}
                    {it.status}
                  </span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{it.reason}</div>
                {it.attachment_path && (
                  <button type="button" onClick={() => openAttachment(it.id)}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-700 hover:underline"
                    data-testid={`absence-attachment-${it.id}`}>
                    <Paperclip className="h-3.5 w-3.5" /> {it.attachment_name || "View leave letter"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </StudentLayout>
  );
}
